/**
 * Max Leverage — the RTL / GUC quick check.
 *
 * `POST /pricing/scenario/max_leverage`: a handful of values in, the resolved
 * borrower class and the product's per-class MAX-LEVERAGE grid out. It offers NO
 * RATE, by design — this is the "what leverage could this sponsor get"
 * conversation, not a quote. The priced surface is the full RTL/GUC sizer.
 *
 * Same normalizer and resolver as `/scenario/rtl` run behind it, so the class
 * math here is identical to what the priced scenario would resolve — this is a
 * shortcut through the same engine, not a second opinion.
 *
 * RENDER-ONLY: the grid rows, the class, the explanation and the disclaimer are
 * all response fields.
 */
import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import SpeedRounded from "@mui/icons-material/SpeedRounded";
import StraightenRounded from "@mui/icons-material/StraightenRounded";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import { sizerApi } from "../../../api/sizerApi";
import {
  SZ,
  SizerShell,
  Workbench,
  ResultsCol,
  FormCard,
  Section,
  Grid,
  Field2,
  NumInput,
  StepperInput,
  MoneyInput,
  SelectInput,
  ChipGroup,
  ActionBar,
  PrimaryButton,
  ActionNote,
  ResultsCard,
  StatusPill,
  EmptyState,
  Badge,
  SizerDevBar,
  useSizerDevTools,
  SizerStateSpy,
} from "../../sizersShared/sizerUi";
import {
  ResultGroup,
  MaxLeverageGrid,
  ClassAnswer,
  ClassFloorLadder,
  ProvenanceList,
  ClassExplanation,
  PayloadWarnings,
  DataChecks,
  ScenarioDisclaimer,
} from "../../sizersShared/sizerResultUi";
import { humanize, parseClassFloors } from "../../sizersShared/sizerFormat";
import {
  selectOptionsLoanPurpose,
  selectOptionsCitizenshipStatus,
  sizerOptionsRtlProductType,
} from "../../sizersShared/sizerSelectOptions";

/**
 * The backend's three purposes. "Delayed Purchase" was retired as a purpose
 * (REQ-106, 2026-10-19) and is now a flag on a cash-out refi; this quick check
 * reads the grid by purpose only and does not ask it — a delayed purchase
 * prices in the full sizers, which do.
 */
const MAX_LEVERAGE_PURPOSES = selectOptionsLoanPurpose;

/**
 * The endpoint's flat body: `{loanProductType, loanPurpose, ficoMid (or
 * bestFico), experienceTotal, totalLiquidity, citizenshipStatus}`.
 *
 * These are SUM-OF-GUARANTOR ROLLUPS, and that is the sanctioned entry style
 * here (2026-08-17): no rate is offered, so the point is not to make anyone
 * build borrower rows — one best FICO and the summed totals are enough. The
 * backend synthesizes a single borrower out of them and runs the same
 * normalizer and resolver as `/scenario/rtl`, so the class math is identical to
 * the priced scenario either way.
 */
const DEFAULTS = {
  loanProductType: "",
  loanPurpose: "",
  ficoMid: "",
  experienceTotal: "",
  totalLiquidity: "",
  citizenshipStatus: "US Citizen",
};

const DEMO = {
  loanProductType: "Fix and Flip",
  loanPurpose: "Purchase",
  ficoMid: 720,
  experienceTotal: 5,
  totalLiquidity: 250000,
  citizenshipStatus: "US Citizen",
};

const num = (v) => (v === "" || v == null ? null : Number(v));

export default function MaxLeverageQuickCheck() {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { spyOpen, toggleSpy } = useSizerDevTools();

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const run = useCallback(() => {
    setBusy(true);
    sizerApi
      .maxLeverage({
        loanProductType: form.loanProductType,
        loanPurpose: form.loanPurpose,
        ficoMid: num(form.ficoMid),
        experienceTotal: num(form.experienceTotal) ?? 0,
        // Cash + other liquidity summed across ALL guarantors. It does not
        // affect the CLASS (credit points + experience points is the whole
        // equation for RTL/GUC) — it is what makes the resolved-liquidity row
        // below a real figure instead of a $0 the response had to invent.
        totalLiquidity: num(form.totalLiquidity) ?? 0,
        citizenshipStatus: form.citizenshipStatus || null,
      })
      .then(setResult)
      .catch((e) =>
        enqueueSnackbar(
          e?.response?.data?.detail || "Max leverage check failed",
          { variant: "error" },
        ),
      )
      .finally(() => setBusy(false));
  }, [form, enqueueSnackbar]);

  const resolved = result?.resolved || {};
  const classification = resolved.classification || {};
  const ready = Boolean(form.loanProductType && form.loanPurpose);

  const rows = result?.maxLeverage || [];
  // Class score floors are versioned policy data quoted inside the response's
  // own explanation sentence — see parseClassFloors. [] when unparseable, which
  // just drops the floor markers rather than inventing thresholds.
  const floors = useMemo(
    () => parseClassFloors(result?.classExplanation),
    [result],
  );
  const resolvedRow = rows.find((r) => r.isResolvedClass) || null;
  const scenarioContext = [result?.product, result?.loanPurpose]
    .filter(Boolean)
    .join(" · ");

  // Cross-highlight: hovering a class in the grid lights its floor on the
  // ladder, and the reverse. They describe the same four classes, so the two
  // read as one control rather than two lists that happen to share labels.
  const [hoverClass, setHoverClass] = useState(null);
  // Off by default — the absolute maxima are the answer; the deltas are the
  // follow-up question ("what does dropping a class cost?").
  const [showDelta, setShowDelta] = useState(false);

  return (
    <SizerShell
      headerAction={
        <SizerDevBar
          variant="menu"
          onFillDemo={() => setForm(DEMO)}
          spyOpen={spyOpen}
          onToggleSpy={toggleSpy}
        />
      }
      title="Max Leverage"
      subtitle="Five inputs to a borrower class and the product's per-class leverage grid. No rate — that's the full sizer."
    >
      <Workbench>
        <Box>
          <FormCard>
            <Section
              first
              title="Sponsor & product"
              subtitle="The same class resolver the priced scenario runs — just without the loan."
            >
              <Grid cols={12}>
                <Field2 label="Product type" span={12}>
                  <ChipGroup
                    sm
                    value={form.loanProductType}
                    onChange={(v) => set("loanProductType", v)}
                    options={sizerOptionsRtlProductType}
                  />
                </Field2>
                <Field2 label="Purpose" span={12}>
                  <ChipGroup
                    sm
                    value={form.loanPurpose}
                    onChange={(v) => set("loanPurpose", v)}
                    options={MAX_LEVERAGE_PURPOSES}
                  />
                </Field2>
              </Grid>

              {/* Two across, not four. These four carry hints, and a quarter of
                  the card is not enough width for a label, a control and a line
                  of explanation without all three wrapping. */}
              <Grid cols={2} sx={{ mt: 3 }}>
                <Field2 label="FICO" hint="Best guarantor FICO.">
                  <NumInput value={form.ficoMid} onChange={(v) => set("ficoMid", v)} />
                </Field2>
                {/* CUMULATIVE across guaranteeing members — the same total the
                    full sizer sums out of the six experience categories. A
                    stepper because it is a small COUNT: the realistic range is
                    nudged, not typed. */}
                <Field2
                  label="Experience (total)"
                  hint="Cumulative deals across all guarantors."
                >
                  <StepperInput
                    value={form.experienceTotal}
                    onChange={(v) => set("experienceTotal", v)}
                    min={0}
                  />
                </Field2>
                {/* Dollars, like every other money field in the kit — it is a
                    balance, and a bare number invited "250" for $250,000. */}
                <Field2
                  label="Total liquidity"
                  hint="Cash + other, all guarantors. Not scored for class."
                >
                  <MoneyInput
                    value={form.totalLiquidity}
                    onChange={(v) => set("totalLiquidity", v)}
                    placeholder="0"
                  />
                </Field2>
                <Field2 label="Citizenship status">
                  <SelectInput
                    value={form.citizenshipStatus ?? ""}
                    onChange={(v) => set("citizenshipStatus", v)}
                    options={selectOptionsCitizenshipStatus}
                  />
                </Field2>
              </Grid>

              <ActionBar>
                <PrimaryButton
                  onClick={run}
                  disabled={busy || !ready}
                  startIcon={
                    busy ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <SpeedRounded />
                    )
                  }
                >
                  {busy ? "Checking…" : "Check max leverage"}
                </PrimaryButton>
                <ActionNote>
                  {ready
                    ? "Preliminary — class and leverage are subject to due diligence."
                    : "Pick a product type and purpose to run the check."}
                </ActionNote>
              </ActionBar>
            </Section>
          </FormCard>

          {rows.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <FormCard>
                <Section
                  first
                  title="Max leverage by class"
                  subtitle={scenarioContext}
                  action={
                    <DeltaToggle
                      checked={showDelta}
                      onChange={() => setShowDelta((v) => !v)}
                    />
                  }
                >
                  <MaxLeverageGrid
                    rows={rows}
                    floors={floors}
                    showDelta={showDelta}
                    hoverClass={hoverClass}
                    onHoverClass={setHoverClass}
                  />
                  <Box sx={{ mt: 2 }}>
                    <ScenarioDisclaimer text={result.disclaimer} />
                  </Box>
                </Section>
              </FormCard>
            </Box>
          )}

          {/* Dev tooling last, under the form it acts on — not in the page
              header, which is about the scenario. */}

          <SizerStateSpy values={form} enabled={spyOpen} />
        </Box>

        <ResultsCol>
          <ResultsCard
            title="Borrower classification"
            pill={
              busy ? (
                <StatusPill state="est">Checking…</StatusPill>
              ) : result ? (
                <StatusPill state="live">Resolved</StatusPill>
              ) : (
                <StatusPill state="idle">Not run</StatusPill>
              )
            }
            foot="No rate is offered here — run the RTL Sizer for pricing."
          >
            {!result ? (
              <EmptyState
                icon={<StraightenRounded />}
                title="Class will appear here"
                description="Enter a FICO and an experience count to resolve the borrower class and its leverage grid."
              />
            ) : (
              <>
                <PayloadWarnings warnings={result.payloadWarnings} />
                {/* The quick check runs the same normalizer as the priced
                    scenario, so it returns the same declared-vs-derived checks
                    — a declared guarantor count that outruns the rows shows up
                    here too. */}
                <DataChecks checks={result.dataChecks} />

                {/* The answer and the leverage it unlocks, in one block. */}
                <ClassAnswer
                  borrowerClass={resolved.borrowerClass}
                  totalScore={classification.totalScore}
                  context={scenarioContext}
                  resolvedRow={resolvedRow}
                />

                {/* Its own rule rather than a ResultGroup: the ladder brings
                    its own heading, and an empty group label would leave a
                    stray gap above it. */}
                <Box sx={{ pt: 2.5, mt: 2.5, borderTop: `1px solid ${SZ.line}` }}>
                  <ClassFloorLadder
                    floors={floors}
                    totalScore={classification.totalScore}
                    creditScore={classification.creditScore}
                    experienceScore={classification.experienceScore}
                    borrowerClass={resolved.borrowerClass}
                    product={result.product}
                    rows={rows}
                    hoverClass={hoverClass}
                    onHoverClass={setHoverClass}
                  />
                </Box>

                <ResultGroup label="Resolved inputs">
                  <ProvenanceList items={provenanceItems(resolved)} />
                  {resolved.isForeignNational && (
                    <Flx sx={{ mt: 1 }}>
                      <Badge tone="fail">Foreign national</Badge>
                    </Flx>
                  )}
                </ResultGroup>

                <ClassExplanation text={result.classExplanation} />
              </>
            )}
          </ResultsCard>
        </ResultsCol>
      </Workbench>
    </SizerShell>
  );
}

/**
 * "Show change vs your class" — a switch, not a button, because it toggles a
 * view of the same rows rather than performing anything.
 */
const DeltaToggle = ({ checked, onChange }) => (
  <Box
    component="label"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.125,
      fontSize: 12,
      fontWeight: 600,
      color: SZ.ink500,
      cursor: "pointer",
      userSelect: "none",
    }}
  >
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      onChange={onChange}
      sx={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
    />
    <Box
      sx={{
        width: 32,
        height: 18,
        borderRadius: "99px",
        flexShrink: 0,
        position: "relative",
        background: checked ? SZ.teal600 : SZ.lineStrong,
        transition: "background .15s",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 2,
          left: checked ? 16 : 3,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,.2)",
          transition: "left .15s",
        },
      }}
    />
    Show change vs your class
  </Box>
);

/**
 * The `ResolvedInputs` block as provenance rows. Each `ResolvedInput` carries
 * `{value, source, reliability, validated}`; `validated` is true only at the
 * VERIFIED precedence, so anything entered or sizer-estimated is tagged
 * Unverified — it prices, but it is not lock-grade.
 *
 * `liquidity` is shown because the response resolves it, with a "Not scored"
 * tag: RTL/GUC classification is credit + experience only (see the equation
 * above), and an unexplained $0 sitting in this list reads like a problem.
 */
const provenanceItems = (resolved) => {
  const items = [];
  const add = (label, ri, extra = {}) => {
    if (!ri) return;
    items.push({
      label,
      value: ri.value == null ? "—" : String(ri.value),
      reliability: ri.reliability,
      tags: [
        { label: humanize(ri.source || "unresolved"), tip: `Source: ${ri.source}` },
        ri.validated
          ? { label: "Verified", tip: "Validated at the verified precedence." }
          : {
              label: "Unverified",
              tone: "warn",
              tip: "Not yet validated in underwriting — prices the scenario, but keeps it out of lock-ready.",
            },
        ...(extra.tags || []),
      ],
      ...extra.rest,
    });
  };

  add("FICO", resolved.fico);
  add("Experience", resolved.experience);
  add("Liquidity", resolved.liquidity, {
    tags: [
      {
        label: "Not scored",
        tone: "muted",
        tip: "Liquidity does not affect borrower classification for this product — the class is credit points plus experience points.",
      },
    ],
  });
  return items;
};
